import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Close from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import EditIcon from '../../../../icons/EditIcon';

import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';

import { COLORS } from '../../../../utils/colors';
import {
  IPromptCard,
  ITextBlock,
  IChecklist,
  IUserDocumentSection,
  IForm,
} from '@homeporter/types';
import { ISelectedCard } from '../../../../global/interfaces';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { RegenerateModalButton } from './RegenerateModalButton';

interface CardContentsProps {
  card: IPromptCard | ITextBlock | IChecklist | IForm;
  index: number;
  selectedCard: ISelectedCard | null;
  styles: {
    cardSelect: object;
    cardContainer: object;
    title: object;
    model: object;
    viewButton: object;
    description: object;
    buttonBox: object;
    loadingBox: object;
    iconButton: object;
    menuBox: object;
    menuOpenIcon: object;
    cardContent: object;
    editingTitle: object;
  };
  showHidden: boolean;
  documentSection: IUserDocumentSection;
  setSelectedCard: (card: ISelectedCard | null) => void;
  setSaveEnabled: (enabled: boolean) => void;
  isGeneratingResponse: boolean;
  errorGeneratingResponse: boolean;
  regenerateIndex: number | undefined;
  setRegenerateIndex: (index: number) => void;
  setEditCardIndex: (index: string) => void;
  cardMenuOpen: boolean[];
  setCardMenuOpen: React.Dispatch<React.SetStateAction<boolean[]>>;
  documentsRef: React.MutableRefObject<any>;
  documentIndex: number;
  setDocumentSection: (section: IUserDocumentSection) => void;
  showGenerateReportPanel: boolean;
}

const CardDetails: React.FC<CardContentsProps> = ({
  card,
  index,
  selectedCard,
  styles,
  documentSection,
  setSelectedCard,
  setSaveEnabled,
  isGeneratingResponse,
  errorGeneratingResponse,
  regenerateIndex,
  setRegenerateIndex,
  setEditCardIndex,
  documentsRef,
  documentIndex,
  setDocumentSection,
  showGenerateReportPanel,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [repaint, setRepaint] = useState<boolean>(false);

  const handleSelectCard = (event: React.MouseEvent) => {
    //event.stopPropagation();

    const targetElement = (event.target as HTMLElement).closest('[id]');
    const targetIndex = Number(targetElement?.id);

    console.log(
      '::::handleSelectCard: ',
      targetIndex,
      'regenerateIndex:',
      regenerateIndex,
    );

    if (
      isNaN(targetIndex) ||
      targetIndex === selectedCard?.index ||
      !documentSection?.cards[targetIndex]
    )
      return;

    setSelectedCard({
      index: Number(targetIndex),
      obj: documentSection?.cards[Number(targetIndex)],
    });
  };

  const deletePromptCard = (index: number) => {
    if (!documentsRef?.current || !documentSection?.cards) return;

    //add deleted state so we can know which prompt card results were not accepted
    console.log('::::deletePromptCard: ', index);

    const currentCard = documentSection?.cards[index];

    const updatedCards = documentSection?.cards.filter(
      (card, cardIndex) => cardIndex !== index,
    );
    if (!updatedCards || !currentCard) return;
    for (const card of updatedCards) {
      if (currentCard.sort === card.sort) {
        card.include = true;
        break;
      }
    }

    const updatedDocumentSection = { ...documentSection };
    updatedDocumentSection.cards = updatedCards;

    documentsRef.current[documentIndex].sections[documentSection.index] =
      updatedDocumentSection;
    console.log('::::setDocument section 16...');
    setDocumentSection(updatedDocumentSection);
    setSaveEnabled(true);
  };

  const downloadChecklist = (card: IChecklist, index: number) => {
    const criteriaList: number[] = [];
    card.data.forEach((sectionEntry) => {
      sectionEntry.children.forEach((child) => {
        for (const item of child.items) {
          criteriaList.push((item.air && !item.uir) || item.uir ? 1 : 0);
        }
      });
    });

    const blob = new Blob([JSON.stringify(criteriaList, null, 2)], {
      type: 'text/plain',
    });
    const title =
      documentsRef.current[documentIndex]?.title.toLowerCase() || '';

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `checklist-${title.replace(
      / /g,
      '-',
    )}-${documentSection.index}-${index}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const calculateScore = (card: IChecklist) => {
    console.log('::::calculateScore card: ', card);

    const validatedChecklist = documentSection?.cards?.find(
      (c) => c.type === 'Checklist' && c.validated,
    ) as IChecklist;

    if (!validatedChecklist) return;
    console.log('::::validatedChecklist: ', validatedChecklist);

    const criteriaList: number[] = [];
    card.data.forEach((sectionEntry) => {
      sectionEntry.children.forEach((child) => {
        for (const item of child.items) {
          criteriaList.push((item.air && !item.uir) || item.uir ? 1 : 0);
        }
      });
    });

    let index = 0;
    let score = 0;
    validatedChecklist.data.forEach((sectionEntry) => {
      sectionEntry.children.forEach((child) => {
        for (const item of child.items) {
          score +=
            ((item.air && !item.uir) || item.uir ? 1 : 0) ===
            criteriaList[index]
              ? 1
              : 0;
        }
      });
    });

    card.score = Math.round((score / criteriaList.length) * 100);
    setSaveEnabled(true);
    setRepaint(!repaint);
  };

  const [visibleButtons, setVisibleButtons] = useState(false);

  const isLoading =
    (isGeneratingResponse || errorGeneratingResponse) &&
    regenerateIndex === index;

  const hasValidatedChecklist =
    card.type === 'Checklist' &&
    !card.validated &&
    documentSection?.cards?.some((c) => c.type === 'Checklist' && c.validated);
  const canEditPrompt = card.include && card.type === 'Prompt' && card.response;

  const canShowActions = !showGenerateReportPanel && card.type !== 'TextBlock';
  useEffect(() => {
    setVisibleButtons(
      !!(isLoading || canShowActions || hasValidatedChecklist || canEditPrompt),
    );
  }, [isLoading, canShowActions, hasValidatedChecklist, canEditPrompt]);

  const buttons = (
    <>
      {card.include &&
        (isLoading ? (
          <Box sx={styles.loadingBox}>
            {isGeneratingResponse ? (
              <CircularProgress size="1.25rem" />
            ) : (
              <b>Error !</b>
            )}
          </Box>
        ) : (
          canShowActions && (
            <>
              <Tooltip title="Regenerate response">
                <span>
                  <RegenerateModalButton
                    card={card}
                    index={index}
                    styles={styles}
                    documentSection={documentSection}
                    regenerateIndex={regenerateIndex}
                    setSelectedCard={setSelectedCard}
                    setRegenerateIndex={setRegenerateIndex}
                    setRepaint={setRepaint}
                    repaint={repaint}
                  />
                </span>
              </Tooltip>

              {card.validated && card.type === 'Checklist' && (
                <Tooltip title="Tooltip #4">
                  <Button
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadChecklist(card as IChecklist, index);
                    }}>
                    Download
                  </Button>
                </Tooltip>
              )}
            </>
          )
        ))}

      {hasValidatedChecklist && (
        <Tooltip title="Calculate score">
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              calculateScore(card);
            }}>
            {`Score: ${card.score || 'n/a'}`}
          </Button>
        </Tooltip>
      )}

      {canEditPrompt && (
        <Tooltip title="Edit prompt">
          <span>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setEditCardIndex(String(index));
              }}
              disabled={regenerateIndex !== undefined}
              sx={styles.iconButton}>
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {!isLoading && canShowActions && (
        <Tooltip
          title={
            !card.validated
              ? 'Lock validated response'
              : 'Unlock validated response'
          }>
          <span style={{ marginLeft: 'auto' }}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                card.validated = !card.validated;
                setSaveEnabled(true);
                setRepaint(!repaint);
              }}
              disabled={regenerateIndex !== undefined}
              sx={styles.iconButton}>
              <VerifiedUserOutlinedIcon
                sx={{ color: card.validated ? '#32CD32' : '#eee' }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </>
  );

  /*
  const userFeedback = (rating: 1 | 0 | undefined) => {
    if (!selectedCard?.obj) return;

    if (selectedCard.obj.rating === rating) {
      selectedCard.obj.rating = undefined;
      console.log('.......');
    } else selectedCard.obj.rating = rating;
    setSaveEnabled(true);

    setSelectedCard({
      ...selectedCard,
    });
  };
*/

  const [editingTitle, setEditingTitle] = useState(false);

  return (
    <Box
      id={String(index)}
      sx={{
        ...(card.include ? {} : { background: COLORS.LIGHT_GRAY }),
        ...styles.cardContainer,
        ...(selectedCard?.index === index && styles.cardSelect),
        ...(card.type === 'TextBlock' && { minHeight: '2.5rem' }),
      }}
      onClick={(e) => handleSelectCard(e)}>
      <Box display={'flex'} alignItems="center">
        {editingTitle ? (
          <input
            style={styles.editingTitle as React.CSSProperties}
            autoFocus
            defaultValue={card.title}
            onBlur={(e) => {
              setEditingTitle(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const oldTitle = card.title;
                documentSection.cards.forEach((c) => {
                  if (c.title === oldTitle) {
                    c.title = e.currentTarget.value;
                  }
                });
                setEditingTitle(false);
                setSaveEnabled(true);
                setDocumentSection({
                  ...documentSection,
                });
              } else if (e.key === 'Escape') {
                return setEditingTitle(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <Typography
            sx={styles.title}
            onClick={(e) => {
              e.stopPropagation();
              setEditingTitle(true);
            }}>
            {card.title}
          </Typography>
        )}{' '}
        {card.type === 'Prompt' && (
          <Typography sx={styles.model}>
            {(card as IPromptCard).model}
          </Typography>
        )}
        {['TextBlock', 'Checklist', 'Form'].includes(card.type || '') && (
          <Typography sx={styles.model}> </Typography>
        )}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          sx={{
            ...styles.viewButton,
          }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
        <Tooltip title={card.include ? 'Hide prompt card' : 'Show prompt card'}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              const includeCard = documentSection?.cards[index];
              if (card.include) {
                card.include = false;
              } else if (includeCard) {
                documentSection?.cards.forEach((card, cardIndex) => {
                  if (card.sort === includeCard.sort)
                    card.include = index === cardIndex;
                });
              }
              setSelectedCard({
                index: index,
                obj: card,
              });
              setSaveEnabled(true);
            }}
            sx={{
              ...styles.viewButton,
            }}>
            {card.include ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
          </IconButton>
        </Tooltip>
        {false && ( //showHidden
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              deletePromptCard(index);
            }}>
            <Close />
          </IconButton>
        )}
      </Box>
      {isExpanded && (
        <Box>
          {card.type === 'Prompt' && card.messagesTemplate && (
            <TextField
              sx={styles.cardContent}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              label="Messages Template"
              value={card.messagesTemplate}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true,
              }}
            />
          )}
          {card.type === 'Prompt' && (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  alignSelf: 'flex-start',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}>
                Model: {(card as IPromptCard).model.toUpperCase()}
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  alignSelf: 'flex-start',
                  mb: '1.5rem',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}>
                Include links to transcript: {card.structured ? 'Yes' : 'No'}
              </Typography>
            </>
          )}
          {card.type === 'TextBlock' && (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  alignSelf: 'flex-start',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}>
                Dynamic variables:
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  alignSelf: 'flex-start',
                  mb: '1.5rem',
                  color: 'text.secondary',
                  fontStyle: 'italic',
                }}>
                <br />

                {
                  '{{session_title}}, {{session_date}}, {{session_time}}, {{location}}, {{address}}, {{city}}, {{province}}, {{postalcode}}, {{mobile_phone}}, {{email}}, {{notes}}'
                }
              </Typography>
            </>
          )}

          {(card.type === 'Checklist' || card.type === 'Form') && (
            <Typography
              variant="subtitle2"
              sx={{
                alignSelf: 'flex-start',
                mb: '1.5rem',
                color: 'text.secondary',
                fontStyle: 'italic',
              }}>
              Algorithm: {(card as IChecklist).algorithm}
            </Typography>
          )}

          {card.type === 'Prompt' && card.context && (
            <TextField
              sx={styles.cardContent}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              label="Context"
              value={card.context}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                readOnly: true,
              }}
            />
          )}
        </Box>
      )}
      {card.type === 'Prompt' && (
        <Box sx={styles.description}>{card.prompt}</Box>
      )}
      {(card.type === 'Checklist' || card.type === 'Form') && (
        <Box sx={styles.description}>{card.name}</Box>
      )}
      {visibleButtons && <Box sx={styles.buttonBox}>{buttons}</Box>}
    </Box>
  );
};

export default CardDetails;
